import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import useForm from '@utils/useForm';
import { Helpers } from '@utils/helpers';
import { GAAviva } from '@utils/gaAviva';
import { get, isEmpty } from 'lodash';
import { useHistory } from 'react-router-dom';
import { useTheme, ITheme } from 'src/theme';
import { useAvivaSelector, useAvivaDispatch } from '@redux/hook';
import { DeclarationForm } from '@models/MLTC/Declaration.model';
import { updateDeclaration, updateReadDocs, completeStep, updatePdfPath } from '@redux/slices/MLTC/step4Slice';
import { postDeclarationPDF, postSavingMyInfo } from '@api/index';
import { IMyInfoQuestionRequest } from '@models/MyInfoRequest.model';
import { setLoadingState } from '@redux/slices/loadingSlice';

import { LeftGrid, RightGrid, FullGrid, EmptyGrid, ContainerGrid, Container } from '@components/LayoutGrid';
import IconDownload from '@assets/icon/download.svg';
import MultiSteps from '@components/MultiSteps';
import Heading from '@components/Heading';
import Text from '@components/Text';
import CheckboxList from '@components/CheckboxList';
import Checkbox from '@components/Checkbox';
import Link from '@components/Link';
import TextCollapsible from '@components/TextCollapsible';
import ContactBox from '@components/ContactBox';
import InformationBox from '@components/InformationBox';
import Button from '@components/Button';
import Divider from '@components/Divider';
import { validate } from './service';
import ContainerBlock from '@components/ContainerBlock';
import Bullet from '@components/Bullet';
import Modal from '@components/Modal';
import { css } from '@linaria/core';
import { UOB_CONTACT_URL } from '@static/config';

const page = 'Declaration';

const lowercase = css`
  text-transform: lowercase;
`

const convertDeclaration = (values: DeclarationForm) => {
  const convertValue = (value: boolean | undefined) => value === true ? 'Y' : 'N';
  return `2,${convertValue(values.summary)}|` +
    `16,${convertValue(values.term)}|` +
    `17,${convertValue(values.declaration)}|` +
    `18,${convertValue(values.required1)}|` +
    `19,${convertValue(values.required2)}|` +
    `20,${convertValue(values.required3)}|`
}
const convertConsent = (values: any) => {
  let customerConsent = `SMS,${values['sms'] ? 'Y' : 'N'}|Phone,${values['phone'] ? 'Y' : 'N'}|Mail,${values['mail'] ? 'Y' : 'N'}`;
  return customerConsent;
}

export interface DeclarationProps {
  theme?: ITheme
}

const trackOnload = (values: any): void => {
  const consents = [
    values['sms'] && 'SMS',
    values['phone'] && 'Phone',
    values['mail'] && 'Mail',
  ].filter(o => o);
  const GA = GAAviva.getInstance();
  let { acquisition, form } = GA.digitalData;
  form.name = 'Declaration';
  acquisition.customer.marketingOptIn = consents;
  GA.trackPageLoad({ form, acquisition });
}
const trackFormSubmmit = (error: string = ''): void => {
  const GA = GAAviva.getInstance();
  if (isEmpty(error)) {
    GA.trackFormSuccess({});
    return;
  }
  const { form } = GA.digitalData;
  form.error.messages = error;
  GA.trackFormFailed({ form });
}

function Declaration(props: DeclarationProps) {
  console.log(`QnB ➤ PAGE ➤ Aviva - ${page}`);
  const history = useHistory();
  const theme: ITheme = useTheme();
  const dispatch = useAvivaDispatch();
  const basicDetails = useAvivaSelector(state => state.mltcStep1.basicDetails);
  const retrievedMyInfo = useAvivaSelector(state => state.mltcStep2.retrievedMyInfo);
  const isStep3Completed = useAvivaSelector(state => state.mltcStep3.isCompleted);
  const initialValue = useAvivaSelector(state => state.mltcStep4.declaration);
  const readDocs = useAvivaSelector(state => state.mltcStep4.readDocs);

  const avivaContactUsUrl = get(window, 'AVIVA_VARIABLE_CONFIG.avivaContactUsUrl');

  const { values, formValid, handleChange, patchValue } = useForm(new DeclarationForm(initialValue), () => { }, validate);

  const [isPageLoaded, setIsPageLoaded] = useState<boolean>(false);
  const [isReadProduct, setIsReadProduct] = useState<boolean>(false);
  const [isReadPolicy, setIsReadPolicy] = useState<boolean>(false);
  const [isReadDeclare, setIsReadDeclare] = useState<boolean>(false);

  const [isReadTerms, setIsReadTerms] = useState<boolean>(false);


  const campaignDetail = useAvivaSelector(state => state.loading.campaignDetail);
  const [campaignCode, setCampaignCode] = useState<string>('MLO1');
  const [isShowUOB, setIsShowUOB] = useState<boolean>(false);
  const gotoUOBContact = () => {
    window.open(UOB_CONTACT_URL, "_blank", "noopener noreferrer");
  };
  const paramCC = campaignDetail?.campaignCode ? `?cc=${campaignDetail.campaignCode}` : '';

  const updateMyInfo = (async () => {
    const id = retrievedMyInfo?.id || 0;
    const declaration = convertDeclaration(values);

    console.debug(values);
    let customerConsent = convertConsent(values);
    console.log('customerConsent', customerConsent);

    const params = new URLSearchParams(window.location.href.split('?')[1]);
    const inject = params.get('inject');
    const payload: IMyInfoQuestionRequest = { id, customerConsent, declaration };
    postSavingMyInfo(payload).then(res => {
      console.debug('postSavingMyInfo', res);
      history.push('/mltc-r2/review' + paramCC + (inject ? '?inject=true' : ''));
    }, error => {
      console.error(error);
      history.push('/mltc-r2/review' + paramCC + (inject ? '?inject=true' : ''));
    })
  });

  const onClickContinue = () => {
    trackOnload(values);
    trackFormSubmmit();
    dispatch(updateDeclaration(values));
    dispatch(updateReadDocs({ isReadPolicy, isReadProduct, isReadTerms, isReadDeclare }));
    dispatch(completeStep(true));
    updateMyInfo();
  }
  const onBack = useCallback((step: number = 1) => {
    history.push(`/mltc/health` + paramCC)
  }, [history])

  const gotoContact = () => {
    if (campaignCode === 'LUB1') {
      setIsShowUOB(true);
    }
    else {
      history.push('/mltc-r2/contact' + paramCC);
    }
  }

  const checkboxHandleChange = (e: any) => {
    const { value, name } = e.target
    console.log(e.target, { [name]: value });
    patchValue({ [name]: value });
  }

  useEffect(() => {
    if (!isPageLoaded) {
      const params = new URLSearchParams(window.location.href.split('?')[1]);
      const inject = params.get('inject');

      console.log(
        'QnB ➤ useEffect() ➤',
        {
          'step1Slice ➤ basicDetails': basicDetails,
          campaignDetail,
          params
        }
      );

      if (campaignDetail) {
        setCampaignCode(campaignDetail?.code);
      }

      if (!isStep3Completed && process.env.REACT_APP_INJECT_CHECK !== 'true') {
        dispatch(completeStep(false));
        onBack(0);
      }
      if (readDocs) {
        const { isReadDeclare, isReadPolicy, isReadProduct, isReadTerms } = readDocs;
        setIsReadDeclare(isReadDeclare);
        setIsReadPolicy(isReadPolicy);
        setIsReadProduct(isReadProduct);
        setIsReadTerms(isReadTerms);
      }

      if (inject === 'true') {
        setIsReadDeclare(true);
        setIsReadPolicy(true);
        setIsReadProduct(true);
        setIsReadTerms(true);
        patchValue({
          summary: true,
          term: true,
          declaration: true,
          required1: true,
          required2: true,
          required3: true,
        })
      }

      trackOnload(values);
      setIsPageLoaded(true);
    }

  }, [readDocs, dispatch, basicDetails, isPageLoaded, isStep3Completed, onBack, values]);


  return (
    <Container>
      <ContainerGrid>
        <FullGrid>
          <MultiSteps steps={5} step={4} label="Declaration" />
          <Heading> Declaration </Heading>
        </FullGrid>
        <LeftGrid>

          <Heading variant="label" margin="0 0 30px"> Please read and agree to all of the following to proceed. </Heading>
          <InformationBox hasClose>
            <Text>Please ensure that you have <b>clicked on the links</b> and read the documents fully before selecting the checkboxes.</Text>
          </InformationBox>
          <CheckboxList margin="0 0 40px">
            <Checkbox name="summary" margin="0 0 30px" checked={values['summary']} onChange={handleChange} disabled={!isReadProduct}>
              <Text>
                I have downloaded, fully read and understood the&nbsp;
                <Link download="product-summary" text="Product summary" target="_blank" rel="noopener noreferrer" url="./docs/mltc/Product-Summary.pdf" isClicked={isReadProduct} callback={() => { setIsReadProduct(true) }} isCheckClicked icon={IconDownload} isEndOfLine={true} slotIcon="end" />
              </Text>
            </Checkbox>

            <Checkbox name="term" margin="0 0 30px" checked={values['term']} onChange={handleChange} disabled={!isReadTerms}>
              <Text>
                I have downloaded, fully read and understood the&nbsp;
                <Link download="terms-and-conditions" text="Policy terms and Conditions*" target="_blank" rel="noopener noreferrer" url="./docs/mltc/Policy-Contract.pdf" isClicked={isReadTerms} callback={() => { setIsReadTerms(true) }} isCheckClicked icon={IconDownload} isEndOfLine={true} slotIcon="end" />
              </Text>
            </Checkbox>

            <Checkbox name="declaration" margin="0 0 30px" checked={values['declaration']} onChange={handleChange} disabled={!isReadDeclare}>
              <Text>
                I have downloaded, fully read and agreed to the statements described in this&nbsp;
                <Link target="_blank" rel="noopener noreferrer" url="./docs/mltc/Declaration.pdf" download="declaration" isClicked={isReadDeclare} callback={() => { setIsReadDeclare(true) }} isCheckClicked text="declaration document" className={lowercase} icon={IconDownload} isEndOfLine={true} slotIcon="end" />
              </Text>
            </Checkbox>

            <Checkbox name="required1" margin="0 0 30px" checked={values['required1']} onChange={handleChange} disabled={!isReadProduct || !isReadTerms || !isReadDeclare}>
              <Text> I have completed and disclosed fully and truthfully all the information requested for in this application.</Text>

            </Checkbox>
            <Checkbox name="required2" margin="0 0 30px" checked={values['required2']} onChange={handleChange} disabled={!isReadProduct || !isReadTerms || !isReadDeclare}>
              <Text> I am purchasing the plan without any advice from a financial adviser representative and I take responsibility to ensure that this plan suits my needs.</Text>
            </Checkbox>
            <Checkbox name="required3" margin="0 0 30px" checked={values['required3']} onChange={handleChange} disabled={!isReadProduct || !isReadTerms || !isReadDeclare}>
              <Text> I am physically present in Singapore at the time of this application submission.</Text>
            </Checkbox>
            <Checkbox name="understand" margin="0 0 30px" checked={values['understand']} onChange={handleChange} disabled={!isReadPolicy || !isReadProduct || !isReadTerms || !isReadDeclare}>
              <Text> I understand that I will receive my policy, any endorsements and communications electronically once they are ready. I understand that I may log on to MySinglife to opt for my preferred document option and may switch between e-documents and printed documents.</Text>
            </Checkbox>
          </CheckboxList>
          <Text margin="0 0 30px">
            <strong>*Important note:</strong>
            <ContainerBlock marginTablet={'10px 0 0 0'}>
              <Bullet>
                <Text>Total distribution cost is only applicable if this product is purchased (with or without financial advice)&nbsp;via Singapore Life Ltd.&apos;s appointed intermediaries or a Financial Adviser Representative is assigned for policy servicing matters.</Text>
              </Bullet>
              <Bullet>
                Total distribution cost is not applicable if this product is purchased directly from Singapore Life Ltd. <strong><u>without financial advice</u></strong> and no Financial Adviser Representative is assigned for policy servicing matters.
              </Bullet>
            </ContainerBlock>
          </Text>

          <Divider size={1} variant="dashed" color={theme.colors.grey[700]} margin="0 0 40px" />
          <Heading variant="sub">Stay in touch with us!</Heading>
          <Text margin="0 0 30px">We’ll update you on special marketing offers, promotions, and information about Singlife’s products and services which may be of interest to you. For details, please refer to <Link target="_blank" rel="noopener noreferrer" url="https://singlife.com/en/pdpa#dpp" text="Singlife’s data protection policy"></Link>. </Text>
          <Text margin="0 0 15px">You consent to receiving marketing updates via:</Text>
          <CheckboxList margin="0 0 24px">
            <Checkbox variant="border" name="mail" onChange={e => checkboxHandleChange(e)}> <Text>Mail or Email</Text> </Checkbox>
            <Checkbox variant="border" name="sms" onChange={e => checkboxHandleChange(e)}> <Text>Messages on any messaging platform (including SMS)</Text> </Checkbox>
            <Checkbox variant="border" name="phone" onChange={e => checkboxHandleChange(e)}> <Text>Telephone Call</Text> </Checkbox>
          </CheckboxList>
          <Text margin="0 0 40px">If you wish to withdraw your consent, you may do so at <Link target="_blank" rel="noopener noreferrer" url={avivaContactUsUrl} text="Singlife’s website" /> at anytime.
          </Text>
          <InformationBox hasClose margin="0 0 40px">
            <Text>
              If you’re unable to complete this Declaration, please leave your contact details <Link text="here" variant="button" onClick={gotoContact} />
              {campaignCode === 'LUB1' ?
                ' and a UOB Bancassurance Specialist will contact you shortly.' :
                ' and our financial adviser representative will be in touch with you shortly.'}
            </Text>
          </InformationBox>
          <Button label="Continue" disabled={!formValid} onClick={onClickContinue} margin="0 0 20px" />
          <Button variant="secondary" onClick={() => onBack(1)} label="Back" margin="0 0 40px" />
        </LeftGrid>
        <EmptyGrid></EmptyGrid>
        {/* <RightGrid>
          <ContactBox items={contactInformation} />
        </RightGrid> */}
        <Modal show={isShowUOB} onClose={() => { setIsShowUOB(false) }}>
          <Text margin={'0px 0 30px'}>You will be redirected to our appointed intermediary - United Overseas Bank Limited (“UOB”) site to submit the contact me form.</Text>
          <Button margin="0 0 20px" label="Proceed" onClick={gotoUOBContact} />
          <Button variant="secondary" label="Cancel" margin={0} onClick={() => { setIsShowUOB(false) }} />
        </Modal>
      </ContainerGrid>
    </Container>
  );
};

export default Declaration;